import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import type { DocumentData, Firestore } from 'firebase/firestore';
import type { StakkoAppId, StakkoNotification } from '../types';

export interface StakkoNotificationInput {
  toUid: string;
  fromUid: string;
  type: string;
  title: string;
  body?: string;
  appId?: StakkoAppId;
  url?: string;
  payload?: Record<string, unknown>;
}

function itemsRef(db: Firestore, uid: string) {
  return collection(db, 'vibiz_notifications', uid, 'items');
}

function toNotification(id: string, data: DocumentData): StakkoNotification {
  return {
    id,
    type: typeof data.type === 'string' ? data.type : 'info',
    title: typeof data.title === 'string' ? data.title : 'Oznámení',
    body: typeof data.body === 'string' ? data.body : null,
    appId: (data.appId ?? undefined) as StakkoAppId | undefined,
    fromUid: typeof data.fromUid === 'string' ? data.fromUid : null,
    url: typeof data.url === 'string' ? data.url : null,
    payload: (data.payload ?? undefined) as Record<string, unknown> | undefined,
    createdAt: data.createdAt ?? null,
    readAt: data.readAt ?? null,
  };
}

/**
 * Pošle notifikaci jinému uživateli (nebo sobě) napříč aplikacemi.
 * Firestore je jediná společná vrstva – appky na různých doménách si
 * nemůžou posílat zprávy přímo.
 */
export async function publishStakkoNotification(
  db: Firestore,
  input: StakkoNotificationInput,
): Promise<string> {
  const ref = doc(itemsRef(db, input.toUid));
  await setDoc(ref, {
    type: input.type,
    title: input.title,
    body: input.body ?? '',
    appId: input.appId ?? null,
    fromUid: input.fromUid,
    url: input.url ?? '',
    payload: input.payload ?? null,
    createdAt: serverTimestamp(),
    readAt: null,
  });
  return ref.id;
}

export function subscribeStakkoNotifications(
  db: Firestore,
  uid: string,
  onData: (notifications: StakkoNotification[]) => void,
): () => void {
  const q = query(itemsRef(db, uid), orderBy('createdAt', 'desc'), limit(30));
  return onSnapshot(
    q,
    (snap) => onData(snap.docs.map((item) => toNotification(item.id, item.data()))),
    (error) => {
      console.warn('[stakko] notifikace se nepodařilo načíst', error);
      onData([]);
    },
  );
}

export async function markStakkoNotificationRead(
  db: Firestore,
  uid: string,
  notificationId: string,
): Promise<void> {
  await updateDoc(doc(db, 'vibiz_notifications', uid, 'items', notificationId), {
    readAt: serverTimestamp(),
  });
}

export async function markAllStakkoNotificationsRead(
  db: Firestore,
  uid: string,
  notifications: StakkoNotification[],
): Promise<void> {
  await Promise.all(
    notifications
      .filter((item) => !item.readAt)
      .map((item) => markStakkoNotificationRead(db, uid, item.id)),
  );
}
